import React from 'react';
import { useNavigate } from 'react-router-dom';
import { WORKFLOWS } from './workflowConfigs';

interface WorkflowSelectionProps {
  /** When provided, called on card click instead of navigating */
  onSelectWorkflow?: (workflowId: string) => void;
  /** Currently selected workflow ID for visual feedback */
  selectedWorkflowId?: string | null;
}

const WorkflowSelection: React.FC<WorkflowSelectionProps> = ({ onSelectWorkflow, selectedWorkflowId }) => {
  const navigate = useNavigate();

  const handleWorkflowSelect = (workflowId: string) => {
    if (onSelectWorkflow) {
      onSelectWorkflow(workflowId);
    } else {
      navigate('/ai-workflow', {
        state: { workflowType: workflowId }
      });
    }
  };

  const isInline = !!onSelectWorkflow;

  return (
    <div className={isInline ? "w-full" : "min-h-screen bg-gray-50/30 p-6"}>
      <div className={isInline ? "w-full max-w-5xl" : "w-full max-w-6xl"}>
        {!isInline && <h1 className="text-xl font-semibold text-gray-900 mb-5">AI Workflows</h1>}
        <div className={`grid gap-4 sm:gap-5 grid-cols-1 sm:grid-cols-2 md:grid-cols-3`}>
          {WORKFLOWS.map((workflow) => (
            <div
              key={workflow.id}
              onClick={() => handleWorkflowSelect(workflow.id)}
              className={`group relative bg-white rounded-xl border-2 shadow-sm overflow-hidden cursor-pointer hover:shadow-md transition-all duration-300 flex flex-col ${selectedWorkflowId === workflow.id
                ? 'border-secondary-500 ring-2 ring-secondary-100'
                : 'border-gray-200 hover:border-gray-300'
                }`}
            >
              {selectedWorkflowId === workflow.id && (
                <div className="absolute top-3 right-3 bg-secondary-500 text-white rounded-full p-1.5 shadow-lg z-10">
                  <i className="fas fa-check text-xs"></i>
                </div>
              )}

              <div className="w-full p-2 bg-gray-50/50 rounded-t-xl text-center">
                <img
                  src={workflow.image}
                  alt={`${workflow.name} Preview`}
                  className="mx-auto h-[220px] sm:h-[260px] w-auto object-contain rounded-lg"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = 'https://via.placeholder.com/400x300?text=Workflow+Preview';
                  }}
                />
              </div>

              <div className="p-3 sm:p-4 bg-white flex flex-col gap-1 flex-shrink-0">
                <h3 className="text-sm sm:text-base font-bold text-gray-900 line-clamp-1">
                  {workflow.name}
                </h3>
                <p className="text-xs text-gray-600 leading-tight line-clamp-2">
                  {workflow.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default WorkflowSelection;
